import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Grid,
  Header,
  Icon,
  Label,
  List,
  Segment,
  Image,
} from 'semantic-ui-react';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import Timestamp from '../Timestamp';
import Phone from '../Phone';
import { formatAmountIntl } from '../../utils/amount';
import userIcon from '../../assets/icons/user.svg';
import { fetchM } from '../../actions/fetchM';

class BankCases extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }

  static propTypes = {
    bankCases: PropTypes.arrayOf(PropTypes.object),
    projectCurrencies: PropTypes.string,
    leadLoanAmount: PropTypes.number,
    opportunityID: PropTypes.string,
    t: PropTypes.func,
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const updates = {};
    if (nextProps.projectCurrencies !== prevState.projectCurrencies) {
      updates.projectCurrencies = nextProps.projectCurrencies || '';
    }
    if (nextProps.bankCases !== prevState.propBankCases) {
      updates.propBankCases = nextProps.bankCases;
      updates.bankCases = nextProps.bankCases || [];
    }
    return Object.keys(updates).length > 0 ? updates : null;
  }

  constructor(props) {
    super(props);
    this.state = {
      projectCurrencies: props.projectCurrencies || '',
      propBankCases: props.bankCases,
      bankCases: props.bankCases || [],
      avatarBlobURLs: {},
      avatarLoading: {},
    };
    this.blobURLs = {};
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchAvatars(this.props.bankCases || []);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.opportunityID !== this.props.opportunityID) {
      this.resetAvatarCaches();
      this.fetchAvatars(this.props.bankCases || []);
      return;
    }
    if (prevProps.bankCases !== this.props.bankCases) {
      this.fetchAvatars(this.props.bankCases || []);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.revokeBlobURLs();
  }

  getCaseText = (status) => {
    switch (status) {
      case 'new': return 'New';
      case 'pending_docs': return 'Pending Docs';
      case 'pending_docs_submitted': return 'Docs Submitted';
      case 'approved_lo_issued': return 'LO Issued';
      case 'pending_unreachable': return 'Unreachable';
      case 'pending_reappealed': return 'Loan Re-appealed';
      case 'pending_meeting' : return 'Pending Meeting';
      case 'meeting_completed': return 'Meeting Completed';
      case 'reject_wrong_info': return 'Wrong Info';
      case 'reject_cancelled': return 'Customer Cancelled';
      case 'success_li_issued': return 'LO Signed';
      case 'reject_unqualified': return 'Unqualified';
      case 'reject_bank': return 'Loan Rejected';
      case 'reject_customer': return 'Customer Rejected';
      case 'reject_lo_signed_other_bank': return 'LO Signed With Another Bank';
      case 'reject_developer': return 'Developer Cancelled';
      default: return '';
    }
  }

  getColor = (status) => {
    if (!status) return 'blue';
    if (status.startsWith('reject')) return 'red';
    if (status.startsWith('pending')) return 'yellow';
    if (status.startsWith('approved') || status.startsWith('success') || status.startsWith('meeting')) return 'green';
    return 'blue';
  }

  revokeBlobURLs = () => {
    Object.values(this.blobURLs).forEach(url => URL.revokeObjectURL(url));
    this.blobURLs = {};
  }

  resetAvatarCaches = () => {
    this.revokeBlobURLs();
    this.setState({ avatarBlobURLs: {}, avatarLoading: {} });
  }

  fetchAvatars = (bankCases) => {
    const { opportunityID } = this.props;
    if (!opportunityID) return;
    const queuedMediaIDs = new Set();
    bankCases.forEach((bankCase) => {
      const mediaID = bankCase.banker && bankCase.banker.profilePictureMediaID;
      if (!mediaID || queuedMediaIDs.has(mediaID)) return;
      if (
        Object.prototype.hasOwnProperty.call(this.state.avatarBlobURLs, mediaID) ||
        this.state.avatarLoading[mediaID]
      ) return;
      queuedMediaIDs.add(mediaID);
      this.fetchAvatar(mediaID, opportunityID);
    });
  }

  fetchAvatar = async (mediaID, opportunityID) => {
    const { store } = this.context;
    if (!store) return;
    this.setState(prevState => ({
      avatarLoading: { ...prevState.avatarLoading, [mediaID]: true },
    }));
    try {
      const res = await store.dispatch(fetchM(
        `/opportunity/${opportunityID}/loan_tracker/lt_shared_media/${mediaID}/`,
        { credentials: 'include', method: 'GET', ignoreNotFound: true },
      ));
      if (!this.mounted) return;
      if (!res.ok) {
        this.setState(prevState => ({
          avatarBlobURLs: { ...prevState.avatarBlobURLs, [mediaID]: null },
          avatarLoading: { ...prevState.avatarLoading, [mediaID]: false },
        }));
        return;
      }
      const blob = await res.blob();
      if (!this.mounted) return;
      const blobURL = URL.createObjectURL(blob);
      this.blobURLs[mediaID] = blobURL;
      this.setState(prevState => ({
        avatarBlobURLs: { ...prevState.avatarBlobURLs, [mediaID]: blobURL },
        avatarLoading: { ...prevState.avatarLoading, [mediaID]: false },
      }));
    } catch (err) {
      if (!this.mounted) return;
      this.setState(prevState => ({
        avatarBlobURLs: { ...prevState.avatarBlobURLs, [mediaID]: null },
        avatarLoading: { ...prevState.avatarLoading, [mediaID]: false },
      }));
    }
  }

  renderBankerAvatar = (bankCase) => {
    const { avatarBlobURLs, avatarLoading } = this.state;
    const mediaID = bankCase.banker && bankCase.banker.profilePictureMediaID;
    const isLoading = mediaID && avatarLoading[mediaID];
    const blobURL = mediaID && avatarBlobURLs[mediaID];
    const src = blobURL || userIcon;

    if (isLoading) {
      return (
        <div
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: '#f3f4f6',
            border: '1px solid rgba(34, 36, 38, 0.08)',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(0,0,0,0.4)',
          }}
        >
          <Icon loading name="spinner" />
        </div>
      );
    }

    const bankerName = bankCase.banker && bankCase.banker.name;
    return (
      <Image
        src={src}
        alt={bankerName || ''}
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          objectFit: 'cover',
          background: '#f3f4f6',
          border: '1px solid rgba(34, 36, 38, 0.08)',
          flexShrink: 0,
        }}
      />
    );
  }

  render() {
    const { t } = this.props;
    const { bankCases, projectCurrencies } = this.state;
    const { leadLoanAmount } = this.props;

    return (
      <Grid columns={2} stretched stackable doubling>
        {bankCases.map((bankCase) => {
          const statusText = t(
            this.getCaseText(bankCase.status), { ns: TRANSLATION_FILES.constants },
          );

          const statusColor = this.getColor(bankCase.status);

          return (
            <Grid.Column key={bankCase.id}>
              <Segment raised padded className="bankcases-segment">
                {/* 1. Header Section */}
                <div className="card-header">
                  <Header as="h4" className="header">
                    {this.renderBankerAvatar(bankCase)}
                    <Header.Content className="header-content">
                      {bankCase.bank ? (bankCase.bank.shortName || bankCase.bank.name) : ''}
                      <Header.Subheader>{bankCase.banker && bankCase.banker.bankBranch ? bankCase.banker.bankBranch.name : ''}</Header.Subheader>
                    </Header.Content>
                  </Header>

                  <Label
                    basic
                    color={statusColor}
                    className="header-status"
                  >
                    {statusText}
                  </Label>
                </div>

                {/* 2. Banker Info Section */}
                <div className="banker-section">
                  <Header as="h4" className="banker-header">{bankCase.banker.name}</Header>
                  <List className="banker-list">
                    <List.Item>
                      <Icon name="phone" className="banker-phone" />
                      <List.Content>
                        <Phone
                          mobilePrefix={bankCase.banker.mobilePrefix}
                          mobile={bankCase.banker.mobile}
                          className="banker-link"
                        />
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="mail outline" />
                      <List.Content>
                        {bankCase.banker.email || '—'}
                      </List.Content>
                    </List.Item>
                  </List>
                </div>

                {/* 3. Footer Section */}
                <div className="footer-section">
                  <div className="footer-loan-container">
                    <div className="footer-title">
                      {t('Loan amount applied', { ns: TRANSLATION_FILES.constants })}
                    </div>
                    <div className="footer-content">
                      {projectCurrencies} {formatAmountIntl(Number(bankCase.loanAmount) || Number(leadLoanAmount) || 0, 'vi-VN')}
                    </div>
                  </div>

                  <div className="footer-update-container">
                    <div className="footer-title">
                      {t('Last updated on', { ns: TRANSLATION_FILES.constants })}
                    </div>
                    <div className="footer-content">
                      <Timestamp
                        timestamp={bankCase.latestUpdateAt || bankCase.createdAt}
                        format="DD/MM/YY hh:mm a"
                      />
                    </div>
                  </div>
                </div>
              </Segment>
            </Grid.Column>
          );
        })}
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(BankCases);



// WEBPACK FOOTER //
// ./src/components/Opportunities/BankCases.jsx